import { useEffect } from "react";
import { dbLocal } from "./db-local";
import { crearEnviador, flushUnaVez, iniciarFlusher } from "./cola";
import { asegurarDuenoCopia, sincronizarCatalogo, sincronizarCopiloto, sincronizarReglas, sincronizarStock } from "./sync";
import { getToken } from "./auth";
import { puedeAbrir } from "./ruta";
import type { Rol } from "./tipos";

// Orquesta la sincronización del POS mientras hay sesión (§9): arranca el flusher de la cola y
// hace pull de catálogo + stock al entrar y cada 15 min. Todo tolera estar offline (no rompe la UI).
//
// El pull (la copia local del mostrador) solo para quien abre el Mostrador (tramo 04 de
// huayruro-orden-y-mapa): el lector nunca vendió y el dueño solo cuando ya eligió en qué botica atiende
// (sin botica, el servidor le responde 400 en catálogo y stock). El flusher sigue para toda sesión: con
// la cola vacía no manda nada, y si quedó algo de antes se envía (y el server decide).
const CADA_MS = 15 * 60 * 1000;

// Quién pide la copia del mostrador: quien puede abrirlo y, si es el dueño, solo con botica elegida.
export function pideCopiaDelMostrador(rol: Rol, boticaDelDueno: string | null): boolean {
  return puedeAbrir(rol, "mostrador") && (rol !== "super_admin" || boticaDelDueno !== null);
}

//
// `dueno` = de quién es la copia (cuenta y botica, tarea 2.6). Si cambió desde la última vez, la copia
// vieja se tira ANTES de pedir la nueva (en ese orden: si fueran a la vez, el stock recién traído se
// borraría). `botica` = la que eligió el dueño en el Mostrador (Kevin, 8-oct-2026); para los demás, null.
export function useSyncPos(activo: boolean, conCopiaDelMostrador: boolean, dueno: string | null = null, botica: string | null = null): void {
  useEffect(() => {
    if (!activo) return;
    const enviar = crearEnviador(getToken);
    const pararFlusher = iniciarFlusher(dbLocal, enviar);
    if (!conCopiaDelMostrador) return pararFlusher;

    const pull = async () => {
      if (dueno) await asegurarDuenoCopia(dbLocal, dueno).catch(() => false);
      void sincronizarCatalogo(dbLocal, getToken, botica).catch(() => {});
      void sincronizarStock(dbLocal, getToken, botica).catch(() => {});
      void sincronizarReglas(dbLocal, getToken).catch(() => {});
      void sincronizarCopiloto(dbLocal, getToken).catch(() => {});
    };
    void pull();
    const timer = setInterval(() => void pull(), CADA_MS);

    return () => {
      pararFlusher();
      clearInterval(timer);
    };
  }, [activo, conCopiaDelMostrador, dueno, botica]);
}

// Empuja la cola de inmediato (tras encolar una venta/quiebre) sin esperar al tick. Idempotente.
export function flushAhora(): void {
  void flushUnaVez(dbLocal, crearEnviador(getToken)).catch(() => {});
}
